import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@ais-ui/custom-hooks';
import { ReportTemplate } from '@ais-ui/websockets';
import { ReportFilters, ReportSortType } from './types';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  type: ReportSortType;
  direction: SortDirection;
}

export const useReportFilters = (
  reportTemplates: ReportTemplate[],
  search: string
) => {
  const debouncedSearch = useDebounce(
    search.length >= 3 ? search : '',
    200,
    ''
  );

  const [reportFilters, setReportFilters] = useState<ReportFilters>({
    status: 'all',
    type: 'all',
    scheduled: 'all',
    reportType: 'all',
    owner: 'all', // Added owner to initial state
  });

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    type: 'date',
    direction: 'desc',
  });

  const filterByStatus = useCallback(
    (
      templates: ReportTemplate[],
      statusFilter: ReportFilters['status']
    ): ReportTemplate[] => {
      if (statusFilter === 'all') {
        return templates;
      }

      return templates.filter((template) => {
        switch (statusFilter) {
          case 'finished':
            return (
              template.tplneweststatus === 'FINISHED' ||
              template.tplneweststatus === 'FINISHED_SCHEDULED'
            );
          case 'failed':
            return template.tplneweststatus === 'FAILED';
          case 'executing':
            return template.tplneweststatus === 'EXECUTING';
          case 'pending':
            return template.tplneweststatus === 'PENDING';
          case 'scheduled':
            return (
              template.isScheduled && template.schedule?.interval !== 'disabled'
            );
          default:
            return true;
        }
      });
    },
    []
  );

  const filterByScheduled = useCallback(
    (
      templates: ReportTemplate[],
      scheduledFilter: ReportFilters['scheduled']
    ): ReportTemplate[] => {
      if (scheduledFilter === 'all') {
        return templates;
      }

      return templates.filter((template) => {
        const isScheduled =
          template.isScheduled && template.schedule?.interval !== 'disabled';

        if (scheduledFilter === 'scheduled') {
          return isScheduled;
        } else if (scheduledFilter === 'unscheduled') {
          return !isScheduled;
        }

        return true;
      });
    },
    []
  );

  const filterByReportType = useCallback(
    (
      templates: ReportTemplate[],
      reportTypeFilter: ReportFilters['reportType']
    ): ReportTemplate[] => {
      if (reportTypeFilter === 'all') {
        return templates;
      }

      return templates.filter((template) => template.type === reportTypeFilter);
    },
    []
  );

  const filterByOwner = useCallback(
    (
      templates: ReportTemplate[],
      ownerFilter: ReportFilters['owner']
    ): ReportTemplate[] => {
      if (!ownerFilter || ownerFilter === 'all') {
        return templates;
      }

      return templates.filter((template) => template.tplOwner === ownerFilter);
    },
    []
  );

  const filterBySearch = useCallback(
    (templates: ReportTemplate[], searchTerm: string): ReportTemplate[] => {
      if (!searchTerm) return templates;

      const lowerSearch = searchTerm.toLowerCase();
      return templates.filter(
        (template) =>
          template.tplName.toLowerCase().includes(lowerSearch) ||
          template.type.toLowerCase().includes(lowerSearch) ||
          template.jobs?.some(
            (job) =>
              job.name.toLowerCase().includes(lowerSearch) ||
              job.uuid.toLowerCase().includes(lowerSearch)
          )
      );
    },
    []
  );

  // Get template UUIDs that have matching jobs (for auto-expand)
  const getTemplatesWithJobMatches = useCallback(
    (templates: ReportTemplate[], searchTerm: string): string[] => {
      if (!searchTerm) return [];

      const lowerSearch = searchTerm.toLowerCase();
      return templates
        .filter((template) =>
          template.jobs?.some(
            (job) =>
              job.name.toLowerCase().includes(lowerSearch) ||
              job.uuid.toLowerCase().includes(lowerSearch)
          )
        )
        .map((template) => template.tplUuid);
    },
    []
  );

  const sortReports = useCallback(
    (templates: ReportTemplate[], config: SortConfig): ReportTemplate[] => {
      const sorted = [...templates];
      const { type: sortBy, direction } = config;

      switch (sortBy) {
        case 'name':
          sorted.sort((a, b) => a.tplName.localeCompare(b.tplName));
          break;
        case 'date':
          sorted.sort(
            (a, b) => b.tplnewestcreationdate - a.tplnewestcreationdate
          );
          break;
        case 'status':
          sorted.sort((a, b) => {
            const statusPriority: Record<string, number> = {
              EXECUTING: 1,
              PENDING: 2,
              FAILED: 3,
              FINISHED: 4,
              FINISHED_SCHEDULED: 5,
              ABORTED: 6,
              NONE: 7,
            };

            const priorityA = statusPriority[a.tplneweststatus] || 8;
            const priorityB = statusPriority[b.tplneweststatus] || 8;

            return priorityA - priorityB;
          });
          break;
      }

      // Apply direction - reverse if descending (except for date which is already desc by default)
      if (direction === 'desc' && sortBy !== 'date') {
        sorted.reverse();
      } else if (direction === 'asc' && sortBy === 'date') {
        sorted.reverse();
      }

      return sorted;
    },
    []
  );

  const filteredAndSortedReports = useMemo(() => {
    if (!reportTemplates) return [];

    let filtered = reportTemplates;

    filtered = filterBySearch(filtered, debouncedSearch);
    filtered = filterByStatus(filtered, reportFilters.status);
    filtered = filterByScheduled(filtered, reportFilters.scheduled);
    filtered = filterByReportType(filtered, reportFilters.reportType);
    filtered = filterByOwner(filtered, reportFilters.owner); // Added owner filtering
    filtered = filtered.filter((template) => template.type !== 'REPLAY_VIDEO');

    return sortReports(filtered, sortConfig);
  }, [
    debouncedSearch,
    filterBySearch,
    filterByStatus,
    filterByScheduled,
    filterByReportType,
    filterByOwner, // Added to dependencies
    reportFilters.status,
    reportFilters.scheduled,
    reportFilters.reportType,
    reportFilters.owner, // Added to dependencies
    reportTemplates,
    sortReports,
    sortConfig,
  ]);

  // Templates that should be auto-expanded due to job matches
  const templatesWithJobMatches = useMemo(() => {
    return getTemplatesWithJobMatches(filteredAndSortedReports, debouncedSearch);
  }, [filteredAndSortedReports, debouncedSearch, getTemplatesWithJobMatches]);

  return {
    filteredAndSortedReports,
    reportFilters,
    setReportFilters,
    sortConfig,
    setSortConfig,
    templatesWithJobMatches,
    searchTerm: debouncedSearch,
  };
};
